import React, { useContext, useRef, useState, useEffect } from 'react';
import { Form, Input, Progress, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import EUserTypes from '../../enums/EUserTypes';

/**
 * Cores de cada fluxo do PWA. Usadas nos cards da Home e herdadas pelas telas
 * do fluxo (cabeçalho, barra de progresso e botão primário), para o operador
 * ter uma pista visual clara de em qual fluxo está.
 *
 * Mantidas em um único lugar (DRY) — a Home e as telas referenciam este mapa.
 */
export const FLOW_COLORS = {
  process: '#1b3663',     // Processamento (azul Bluquimi)
  conference: '#2f7d32',  // Conferência de Compra (verde)
  drenagem: '#0a7c8a',    // Descarga (ciano)
  amarelamento: '#a16207', // Amarelamento (âmbar/amarelo — solvente que amarelou)
  rc: '#8a5a00',          // Recarga/RC — saldo diário (âmbar)
  rcConsumption: '#b45309', // Adicionar RC à máquina (âmbar-escuro)
  yellowConsumption: '#d97706', // Recarga de Solvente Amarelo (dourado — remete ao amarelo)
  tanque: '#4338ca',      // Leitura do tanque (índigo)
  correction: '#7c3aed',  // Corrigir tambores (violeta — fluxo de revisão)
};

const DEFAULT_COLOR = '#1b3663';

/**
 * Cabeçalho compacto de wizard otimizado para mobile/PWA.
 *
 * Substitui o <Steps> do Ant Design (que se espreme em 375px) por:
 *  - linha com o código do tambor + "Etapa X de N"
 *  - barra de progresso fina na cor do fluxo
 *  - título da etapa atual em destaque
 *
 * `color` (default azul Bluquimi) faz o header herdar a cor do card do fluxo.
 */
export const WizardHeader = ({ drumCode, current, total, title, color = DEFAULT_COLOR }) => {
  const percent = Math.round(((current + 1) / total) * 100);
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <strong style={{ fontSize: 16, color }}>{drumCode}</strong>
        <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
          Etapa {current + 1} de {total}
        </span>
      </div>
      <Progress
        percent={percent}
        showInfo={false}
        strokeColor={color}
        trailColor="#e5e7eb"
        style={{ margin: '6px 0 10px' }}
      />
      <div style={{ fontSize: 18, fontWeight: 600, color }}>{title}</div>
    </div>
  );
};

/**
 * Cabeçalho compacto de tela única (sem steps), no mesmo padrão visual do
 * WizardHeader — usado pelas telas de RC e Descarga no PWA para manter
 * consistência com Processamento/Conferência.
 */
export const ScreenHeader = ({ title, subtitle, color = DEFAULT_COLOR }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ fontSize: 18, fontWeight: 600, color }}>{title}</div>
    {subtitle && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{subtitle}</div>}
  </div>
);

/**
 * Estilo inline para "tintir" o botão primário do Ant com a cor do fluxo.
 * Aplicar via <Button type="primary" style={primaryButtonStyle(color)}>.
 */
export const primaryButtonStyle = (color = DEFAULT_COLOR) => ({
  background: color,
  borderColor: color,
});

/**
 * Botão "Voltar para a Home" usado no topo das telas de fluxo do PWA.
 *
 * Como o OperatorLayout não tem mais header/top bar, a navegação de volta
 * para o hub do operador (/home) vive dentro do conteúdo de cada tela.
 * Herda a cor do fluxo (quando passada) para manter o destaque visual.
 */
export const BackToHome = ({ label = 'Voltar para a Home', color = DEFAULT_COLOR }) => {
  const navigate = useNavigate();
  return (
    <Button
      type="text"
      icon={<ArrowLeftOutlined style={{ fontSize: 16 }} />}
      onClick={() => navigate('/home')}
      aria-label={label}
      style={{ padding: 0, color, fontWeight: 600, marginBottom: 8 }}
    >
      {label}
    </Button>
  );
};

// Detecção de dispositivo touch (iPad/tablet Android/celular). Usada para só
// ativar o keypad customizado em aparelhos touch — no desktop admin o campo
// continua com o teclado físico nativo (inputMode="decimal").
const IS_TOUCH = typeof window !== 'undefined'
  && ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);

// Setter nativo de value do <input>. Usado para o AntD/React aceitar a mudança
// programática de valor no Safari/iPad (setar el.value direto nem sempre
// dispara o onChange controlado do React; o setter do protótipo + evento
// 'input' é o modo confiável).
const NATIVE_VALUE_SETTER = typeof window !== 'undefined'
  ? Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set
  : undefined;

/**
 * Keypad numérico customizado (estilo calculadora) renderizado como overlay
 * fixo no rodapé. Usado pelo DecimalInput em aparelhos touch para garantir um
 * teclado só de dígitos + vírgula mesmo no iPad, cujo teclado nativo sempre
 * mostra números + símbolos.
 *
 * Importante: o React anexa onTouchStart como listener passivo, então
 * e.preventDefault() ali não funciona e o input perdia o foco ao tocar no
 * keypad (o onBlur fechava o teclado em toques rápidos). Por isso o container
 * recebe um listener NATIVO não-passivo de touchstart (addEventListener) que
 * faz o preventDefault de verdade, impedindo o blur. As teclas agem no
 * pointerdown (unifica mouse/touch; sem onClick para não disparar duas vezes).
 */
const Keypad = ({ onKey, onBackspace, onClose }) => {
  const containerRef = useRef(null);
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', ',', '0', '⌫'];
  const handle = (k) => (k === '⌫' ? onBackspace() : onKey(k));

  // Listener nativo não-passivo: impede que tocar no keypad tire o foco do
  // campo (e também impede scroll/gestos dentro do keypad).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const prevent = (e) => e.preventDefault();
    el.addEventListener('touchstart', prevent, { passive: false });
    el.addEventListener('touchmove', prevent, { passive: false });
    return () => {
      el.removeEventListener('touchstart', prevent);
      el.removeEventListener('touchmove', prevent);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        background: '#fff',
        borderTop: '1px solid #e5e7eb',
        boxShadow: '0 -6px 18px rgba(0,0,0,0.15)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      role="group"
      aria-label="Teclado numérico"
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 8px' }}>
        <button
          type="button"
          onPointerDown={(e) => { e.preventDefault(); onClose(); }}
          aria-label="Concluir"
          style={{ border: 'none', background: 'transparent', color: '#1b3663', fontWeight: 700, fontSize: 15, padding: '6px 10px' }}
        >
          Concluir
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, padding: '0 8px 10px' }}>
        {keys.map((k) => (
          <button
            key={k}
            type="button"
            onPointerDown={(e) => { e.preventDefault(); handle(k); }}
            aria-label={k === '⌫' ? 'Apagar' : k === ',' ? 'Vírgula decimal' : `Inserir ${k}`}
            style={{
              height: 52,
              fontSize: 22,
              fontWeight: 600,
              border: '1px solid #e5e7eb',
              borderRadius: 10,
              background: k === '⌫' ? '#f3f4f6' : '#fff',
              color: '#1f2937',
              cursor: 'pointer',
              touchAction: 'manipulation',
            }}
          >
            {k}
          </button>
        ))}
      </div>
    </div>
  );
};

// --- Helpers puros para o DecimalInput (texto cru, sem máscara) ---

// Converte o valor (número/null/string) em texto exibido com vírgula decimal.
const valueToText = (v) => {
  if (v == null || v === '') return '';
  return String(v).replace('.', ',');
};

// Converte o texto cru (ex.: "150,5") em número (150.5) ou null se vazio/inválido.
const textToNumber = (s) => {
  if (s == null || s === '' || s === ',') return null;
  const num = Number(String(s).replace(',', '.'));
  return Number.isNaN(num) ? null : num;
};

// Mantém só dígitos e no máximo uma vírgula (filtro anti-colar símbolos/letras).
const filterDecimal = (raw) => {
  let s = String(raw ?? '').replace(/[^\d,]/g, '');
  const parts = s.split(',');
  if (parts.length > 2) s = `${parts[0]},${parts.slice(1).join('')}`;
  return s;
};

/**
 * Input numérico do PWA com teclado estilo calculadora e SEM máscara automática.
 *
 * Usa um Input de texto (não InputNumber) que aceita somente dígitos + uma
 * vírgula decimal — nenhuma formatação/reformatação while typing. O valor do
 * Form continua sendo um número (convertido em commit), então o resto do fluxo
 * não muda.
 *
 * Em aparelhos touch (iPad, tablet Android, celular): inputMode="none" suprime
 * o teclado nativo (no iPad ele sempre mostra números + símbolos) e abre um
 * keypad customizado. No desktop admin cai para inputMode="decimal" (teclado
 * físico), sem keypad.
 *
 * A inserção pelo keypad usa o setter nativo de value + disparo de evento
 * 'input' (modo confiável no Safari/iPad para o React/AntD aceitar a mudança).
 *
 * Props: value, onChange, min, max + as do Input do Ant (size, prefix, style,
 * autoFocus, aria-label, etc.).
 */
export const DecimalInput = ({ value, onChange, onFocus, onBlur, min, max, ...props }) => {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => valueToText(value));
  // Guarda o último valor que emitimos via onChange, para só sincronizar o
  // texto quando o value vier de fora (reset/edição) e não ecoar nossa própria
  // mudança — isso preserva a vírgula temporária (ex.: "150,") durante a digitação.
  const lastEmittedRef = useRef(value);

  // O ref do Input do AntD aponta para a instância do rc-input (que tem .input),
  // não para o <input> DOM. Acessamos o elemento real por aqui.
  const getEl = () => {
    const r = inputRef.current;
    return r?.input ?? r;
  };

  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      setText(valueToText(value));
      lastEmittedRef.current = value;
    }
  }, [value]);

  // Filtra o texto cru, atualiza o estado e emite o número para o Form.
  const commit = (raw) => {
    const filtered = filterDecimal(raw);
    const num = textToNumber(filtered);
    setText(filtered);
    lastEmittedRef.current = num;
    if (onChange) onChange(num);
  };

  const applyChange = (mutator) => {
    const el = getEl();
    if (!el) return;
    if (document.activeElement !== el) el.focus();
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const { value: next, pos } = mutator(el.value, start, end);
    if (NATIVE_VALUE_SETTER) {
      NATIVE_VALUE_SETTER.call(el, next);
    } else {
      el.value = next;
    }
    el.dispatchEvent(new Event('input', { bubbles: true }));
    try { el.setSelectionRange(pos, pos); } catch (_) { /* alguns browsers ignoram */ }
  };

  // Esvazia o campo ao focar se estiver com zero ("0", "0,0", "0,00"...), mas
  // preserva valores reais como "0,5". Assim o operador digita sem apagar o 0.
  const clearIfZero = () => {
    const el = getEl();
    if (!el) return;
    const t = (el.value ?? '').trim();
    if (t === '0' || /^0,0*$/.test(t)) {
      setText('');
      lastEmittedRef.current = null;
      if (onChange) onChange(null);
      if (NATIVE_VALUE_SETTER) NATIVE_VALUE_SETTER.call(el, '');
      else el.value = '';
    }
  };

  const insertChar = (ch) => applyChange((val, s, e) => {
    // Evita duas vírgulas decimais no mesmo número.
    if (ch === ',') {
      const before = val.slice(0, e);
      if (before.includes(',')) return { value: val, pos: e };
    }
    const next = val.slice(0, s) + ch + val.slice(e);
    return { value: next, pos: s + ch.length };
  });

  const backspace = () => applyChange((val, s, e) => {
    if (s !== e) return { value: val.slice(0, s) + val.slice(e), pos: s };
    if (s <= 0) return { value: val, pos: 0 };
    return { value: val.slice(0, s - 1) + val.slice(s), pos: s - 1 };
  });

  const handleBlur = (e) => {
    // Clamp min/max só no blur (não atrapalha a digitação).
    const num = textToNumber(text);
    if (num != null) {
      let clamped = num;
      if (min != null && num < min) clamped = min;
      if (max != null && num > max) clamped = max;
      if (clamped !== num) {
        setText(valueToText(clamped));
        lastEmittedRef.current = clamped;
        if (onChange) onChange(clamped);
      }
    }
    setOpen(false);
    onBlur?.(e);
  };

  const close = () => {
    setOpen(false);
    getEl()?.blur();
  };

  return (
    <>
      <Input
        ref={inputRef}
        value={text}
        onChange={(e) => commit(e.target.value)}
        inputMode={IS_TOUCH ? 'none' : 'decimal'}
        onFocus={(e) => { if (IS_TOUCH) setOpen(true); clearIfZero(); onFocus?.(e); }}
        onBlur={handleBlur}
        {...props}
      />
      {open && (
        <Keypad
          onKey={insertChar}
          onBackspace={backspace}
          onClose={close}
        />
      )}
    </>
  );
};

/**
 * Campo único de quantidade para o wizard (um campo por passo no mobile).
 *
 * - Teclado numérico: em aparelhos touch (iPad/tablet/celular) o DecimalInput
 *   abre um keypad customizado estilo calculadora (só dígitos + vírgula),
 *   suprimindo o teclado nativo — no iPad o nativo mostra números + símbolos.
 *   No desktop admin usa o teclado físico.
 * - Input grande (altura 56px, fonte 26px), full-width, touch-friendly.
 * - `prefix` legível (L ou °C) dentro do campo via prefix do Ant.
 *
 * Props:
 *  - form: instância de Form
 *  - name: nome do campo no Form
 *  - label: rótulo acima do campo
 *  - unit: 'L' (default) ou '°C'
 *  - min, max, step
 *  - hint: texto de ajuda abaixo do campo
 *  - autoFocus: foca o campo ao montar a etapa
 */
export const QuantityStep = ({
  form,
  name,
  label,
  unit = 'L',
  min = 0,
  max,
  step = 0.1,
  hint,
  autoFocus = true,
}) => {
  return (
    <div>
      <div style={{ fontSize: 15, marginBottom: 8, color: '#374151' }}>{label}</div>
      <Form.Item
        name={name}
        rules={[{ required: true, message: `Informe ${label}.` }]}
      >
        <DecimalInput
          min={min}
          max={max}
          step={step}
          size="large"
          prefix={unit}
          style={{ fontSize: 26, height: 56 }}
          autoFocus={autoFocus}
          aria-label={label}
        />
      </Form.Item>
      {hint && <div style={{ color: '#6b7280', fontSize: 13 }}>{hint}</div>}
    </div>
  );
};

/** Hook auxiliar: indica se o usuário logado é operador/líder (mobile-first no PWA).
 *  LIDER herda a experiência mobile-first do operador (wizard, botões grandes,
 *  botão Voltar à Home), já que usa o PWA no celular/tablet. O Admin do painel
 *  (PANEL_ADMIN) fica de fora, mantendo o layout desktop no InternalLayout. */
export const useIsOperator = () => {
  const { loggedUser } = useContext(GeneralContext);
  return [EUserTypes.OPERADOR.code, EUserTypes.OPERADOR_COMPRADOR.code, EUserTypes.LIDER.code].includes(loggedUser?.type);
};
